import axios from 'axios';

const API = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' }
});

// Attach token to every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Auto logout on 401
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.clear();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const login          = (data) => API.post('/auth/login', data);
export const getMe          = ()     => API.get('/auth/me');
export const forgotPassword = (data) => API.post('/auth/forgot-password', data);
export const resetPassword  = (data) => API.post('/auth/reset-password', data);

// Dashboard
export const getDashboard = () => API.get('/reports/dashboard');

// Clients
export const getClients       = (params) => API.get('/clients', { params });
export const createClient     = (data)   => API.post('/clients', data);
export const getClient        = (id)     => API.get(`/clients/${id}`);
export const getClientSummary = (id)     => API.get(`/clients/${id}/summary`);
export const getRateCards     = (id)     => API.get(`/clients/${id}/rate-cards`);

// Candidates
export const getCandidates  = (params) => API.get('/candidates', { params });
export const createCandidate= (data)   => API.post('/candidates', data);
export const getCandidate   = (id)     => API.get(`/candidates/${id}`);
export const getBench       = (params) => API.get('/candidates/bench', { params });

// Jobs
export const getJobs        = (params) => API.get('/jobs', { params });
export const createJob      = (data)   => API.post('/jobs', data);
export const getJob         = (id)     => API.get(`/jobs/${id}`);
export const matchCandidates= (id)     => API.get(`/jobs/${id}/match`);

// Deployments
export const getDeployments  = (params) => API.get('/deployments', { params });
export const createDeployment= (data)   => API.post('/deployments', data);
export const getDeployment      = (id)       => API.get(`/deployments/${id}`);
export const recordCandidateExit = (id, data) => API.post(`/candidates/${id}/exit`, data);
export const updateFnfStatus     = (id, data) => API.patch(`/candidates/${id}/fnf`, data);
export const updateDeployment= (id, data) => API.put(`/deployments/${id}`, data);
export const getDepStats     = ()       => API.get('/deployments/stats');
export const extendDeployment= (id, data) => API.patch(`/deployments/${id}/extend`, data);
export const terminateDeployment = (id, data) => API.patch(`/deployments/${id}/terminate`, data);

// Attendance
export const markAttendance       = (data)          => API.post('/attendance/mark', data);
export const bulkMarkAttendance   = (data)          => API.post('/attendance/bulk-mark', data);
export const getAttendanceRegister= (params)        => API.get('/attendance/register', { params });
export const getAttendance        = (id, params)    => API.get(`/attendance/deployment/${id}`, { params });

// Timesheets
export const getTimesheets        = (params)        => API.get('/attendance/timesheets', { params });
export const generateTimesheet    = (deploymentId, data) => API.post(`/attendance/timesheets/generate/${deploymentId}`, data);
export const submitTimesheet      = (id)            => API.patch(`/attendance/timesheets/${id}/submit`);
export const approveTimesheet     = (id)            => API.patch(`/attendance/timesheets/${id}/approve`);

// Holidays
export const getHolidays          = (year)       => API.get('/leave-setup/holidays', { params: { year } });
export const createHoliday        = (data)       => API.post('/leave-setup/holidays', data);
export const deleteHoliday        = (id)         => API.delete(`/leave-setup/holidays/${id}`);

// Leave Balance Setup
export const getLeaveBalancesSummary = (year)    => API.get('/leave-setup/leave-balances', { params: { year } });
export const initLeaveBalances    = (data)       => API.post('/leave-setup/leave-balances/init', data);
export const updateLeaveBalance   = (id, data)   => API.patch(`/leave-setup/leave-balances/${id}`, data);

// Leaves
export const applyLeave           = (data)          => API.post('/attendance/leaves', data);
export const getLeaveRequests     = (params)        => API.get('/attendance/leaves', { params });
export const getLeaveBalances     = (candidateId, year) => API.get(`/attendance/leaves/balances/${candidateId}`, { params: { year } });
export const reviewLeave          = (id, data)      => API.patch(`/attendance/leaves/${id}/review`, data);

// Payroll
export const getPayrolls        = (params)     => API.get('/payroll', { params });
export const processPayroll     = (data)       => API.post('/payroll/process', data);
export const bulkProcessPayroll = (data)       => API.post('/payroll/bulk-process', data);
export const approvePayroll     = (id)         => API.patch(`/payroll/${id}/approve`);
export const markPayrollPaid    = (id, data)   => API.patch(`/payroll/${id}/pay`, data);
export const getPayslipPdf      = (id)         => API.get(`/payroll/${id}/payslip-pdf`, { responseType: 'blob' });

// Payroll Register & Reports (Phase 5)
export const getPayrollRegister    = (params)  => API.get('/payroll/register', { params });
export const downloadRegisterCSV   = (params)  => API.get('/payroll/register/export', { params, responseType: 'blob' });
export const downloadBankFile      = (params)  => API.get('/payroll/bank-file', { params, responseType: 'blob' });
export const getAnnualSummary      = (params)  => API.get('/payroll/annual-summary', { params });
export const downloadAnnualCSV     = (params)  => API.get('/payroll/annual-summary/export', { params, responseType: 'blob' });
export const getPayrollTrend       = (params)  => API.get('/payroll/trend', { params });

// Advances & Loans
export const getAdvances        = (params)     => API.get('/payroll/advances', { params });
export const requestAdvance     = (data)       => API.post('/payroll/advances', data);
export const reviewAdvance      = (id, data)   => API.patch(`/payroll/advances/${id}/review`, data);

// Arrears
export const getArrears         = (params)     => API.get('/payroll/arrears', { params });
export const createArrear       = (data)       => API.post('/payroll/arrears', data);
export const reviewArrear       = (id, data)   => API.patch(`/payroll/arrears/${id}/review`, data);
export const markArrearPaid     = (id, data)   => API.patch(`/payroll/arrears/${id}/pay`, data);

// Reimbursements
export const getReimbursements  = (params)     => API.get('/payroll/reimbursements', { params });
export const createReimbursement= (data)       => API.post('/payroll/reimbursements', data);
export const reviewReimbursement= (id, data)   => API.patch(`/payroll/reimbursements/${id}/review`, data);
export const markReimbursementPaid = (id, data)=> API.patch(`/payroll/reimbursements/${id}/pay`, data);

// Salary Components & Templates
export const getSalaryComponents  = ()       => API.get('/salary/components');
export const createSalaryComponent = (data)  => API.post('/salary/components', data);
export const updateSalaryComponent = (id, data) => API.put(`/salary/components/${id}`, data);
export const deleteSalaryComponent = (id)    => API.delete(`/salary/components/${id}`);
export const getSalaryTemplates   = ()       => API.get('/salary/templates');
export const getSalaryTemplate    = (id)     => API.get(`/salary/templates/${id}`);
export const createSalaryTemplate = (data)   => API.post('/salary/templates', data);
export const updateSalaryTemplate = (id, data) => API.put(`/salary/templates/${id}`, data);
export const deleteSalaryTemplate = (id)     => API.delete(`/salary/templates/${id}`);
export const previewSalary        = (data)   => API.post('/salary/preview', data);
export const assignSalaryTemplate  = (deploymentId, data) => API.patch(`/salary/deployments/${deploymentId}/template`, data);
export const getSalaryRevisions    = (deploymentId)       => API.get(`/salary/deployments/${deploymentId}/revisions`);

// Statutory Compliance
export const getPFSummary      = (params) => API.get('/compliance/pf/summary',  { params });
export const getESISummary     = (params) => API.get('/compliance/esi/summary', { params });
export const getPTSummary      = (params) => API.get('/compliance/pt/summary',  { params });
export const getTDSSummary     = (params) => API.get('/compliance/tds/summary', { params });
export const downloadCompliance = (path, params) => API.get(path, { params, responseType: 'blob' });

// Invoices
export const getInvoices     = (params) => API.get('/invoices', { params });
export const getARSummary    = ()       => API.get('/invoices/ar-summary');

// Notifications
export const getInbox        = ()       => API.get('/notifications/inbox');
export const markRead        = (id)     => API.patch(`/notifications/inbox/${id}/read`);

export default API;